import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import {
  NgbCalendar,
  NgbDate,
  NgbDateParserFormatter,
  NgbDatepickerConfig,
  NgbInputDatepicker
} from '@ng-bootstrap/ng-bootstrap';
import { select, Store } from '@ngrx/store';
import { StatisticsFilter } from '../../../../redux/models/statistics/statistics-filter';
import { getCustomerConfigState, State } from '../../../../redux/reducers';
import { CustomDateParserFormatter } from '../utils/custom-date-parser-formatter';
import {
  isCurrentChartEmpty,
  isTwoYearsAgoChartEmpty,
  isOneYearAgoChartEmpty,
  selectAllOfficeIds,
  isOfficesRequestSending
} from '../redux/selectors/dashboard-selector';
import {
  ClearAllStatistics,
  RequestBookingOffices,
  SetCurrentChart,
  SetOneYearAgoChart,
  SetTopRates,
  SetTwoYearsAgoChart,
  StoreStatisticsFilters
} from '../redux/actions/actions-dashboard';
import { Observable } from 'rxjs';
import { take } from 'rxjs/operators';
import { DestinationType } from '../../../../redux/models/statistics/destination-type';
import { isOn } from 'feature-toggle-service';
import { ICompanyNameAndOffice } from '../models/dashboard.model';

@Component({
  standalone: false,
  selector: 'app-kpi-filter',
  templateUrl: './kpi-filter.component.html',
  styleUrls: ['./kpi-filter.component.scss'],
  providers: [{ provide: NgbDateParserFormatter, useClass: CustomDateParserFormatter }]
})
export class KPIFilterComponent implements OnInit {
  @Output()
  filterChanged: EventEmitter<StatisticsFilter> = new EventEmitter<StatisticsFilter>();

  readonly formDefaultValues = {
    officeIds: [],
    contentType: 'Hotel',
    startDate: null,
    endDate: null,
    comparePeriodYearOffset: 0
  };

  readonly formDestinationDefaultValues = {
    code: '',
    name: ''
  };

  readonly THREE_LETTER_CODE = /^[A-Z]{3}$/;
  readonly TWO_LETTER_CODE = /^[A-Z]{2}$/;
  public toggleNewUi = isOn('AD_AVC_DASHBOARD_NEW_HIERARCHY');
  filterControlGroup: FormGroup;
  selectedOffices: ICompanyNameAndOffice[] | string[];

  fromDate: NgbDate | null;
  toDate: NgbDate | null;
  hoveredDate: NgbDate | null = null;
  startDate: NgbDate | null = null;

  allOffices$: Observable<ICompanyNameAndOffice[] | string[]>;
  maxMultiselect = 1;
  defaultOffices: ICompanyNameAndOffice[] | string[];

  currentStatisticsEmpty$: Observable<boolean>;
  oneYearAgoStatisticsEmpty$: Observable<boolean>;
  twoYearsAgoStatisticsEmpty$: Observable<boolean>;
  officesRequestSending$: Observable<boolean>;

  private _appliedFilter: StatisticsFilter;

  constructor(
    private readonly calendar: NgbCalendar,
    public readonly ngbDateFormatter: NgbDateParserFormatter,
    private readonly dpConfig: NgbDatepickerConfig,
    private readonly store: Store<State>,
    private readonly formBuilder: FormBuilder
  ) { }

  get appliedFilter(): StatisticsFilter {
    return JSON.parse(JSON.stringify(this._appliedFilter));
  }

  private set appliedFilter(filter: StatisticsFilter) {
    this._appliedFilter = filter;

    this.filterChanged.emit(JSON.parse(JSON.stringify(this._appliedFilter)));
  }

  onDateSelection(date: NgbDate) {
    if (!this.fromDate && !this.toDate) {
      this.fromDate = date;
      this.filterControlGroup.controls['startDate'].setValue(this.ngbDateFormatter.format(date));
    } else if (this.fromDate && !this.toDate && (date.equals(this.fromDate) || date.after(this.fromDate))) {
      this.toDate = date;
      this.filterControlGroup.controls['endDate'].setValue(this.ngbDateFormatter.format(date));
    } else {
      this.toDate = null;
      this.fromDate = date;
      this.filterControlGroup.controls['startDate'].setValue(this.ngbDateFormatter.format(date));
      this.filterControlGroup.controls['endDate'].setValue(this.toDate);
    }
  }

  isSelected(date: NgbDate): boolean {
    return date.equals(this.fromDate) || date.equals(this.toDate);
  }

  isFrom(date: NgbDate): boolean {
    return date.equals(this.fromDate) && !date.equals(this.toDate);
  }

  isTo(date: NgbDate): boolean {
    const toDate = this.effectiveToDate();
    return !date.equals(this.fromDate) && date.equals(toDate);
  }

  isBetween(date: NgbDate): boolean {
    const toDate = this.effectiveToDate();
    return date.after(this.fromDate) && date.before(toDate);
  }

  effectiveToDate(): NgbDate | null {
    return !this.toDate && this.hoveredDate ? this.hoveredDate : this.toDate;
  }

  // officeId is the same if its string value is the same
  trackByFunc(_index: number, value: string) {
    // NOSONAR
    return value;
  }

  onOfficeSelectionChange(input) {
    if (input instanceof Array && input.length > 1) {
      const mainOfficeId: string | { companyName: string; officeId: string } = this.defaultOffices[0];
      const indexOfMainOfficeId = input.indexOf(mainOfficeId);

      if (indexOfMainOfficeId > 0) {
        this.selectedOffices.splice(indexOfMainOfficeId, 1);
        if (typeof mainOfficeId === 'string' && typeof this.selectedOffices[0] === 'string') {
          (this.selectedOffices as string[]).unshift(mainOfficeId);
        } else if (typeof mainOfficeId === 'object' && typeof this.selectedOffices[0] === 'object') {
          (this.selectedOffices as { companyName: string; officeId: string }[]).unshift(mainOfficeId);
        }
      }
    }
  }

  getSelectedOfficeIdsForPrint(): string {
    let result = '';
    if (!this.selectedOffices) {
      return result;
    }

    if (this.toggleNewUi) {
      this.selectedOffices.forEach((value, index) => {
        if (index === 0) {
          result += `${value.companyName} ${value.officeId}`;
        } else if (index % 15 === 0 && index > 0) {
          result += ',<br/>' + `${value.companyName} ${value.officeId}`;
        } else {
          result += ', ' + `${value.companyName} ${value.officeId}`;
        }
      });
    } else {
      this.selectedOffices.forEach((value, index) => {
        if (index === 0) {
          result += value;
        } else if (index % 15 === 0 && index > 0) {
          result += ',<br/>' + value;
        } else {
          result += ', ' + value;
        }
      });
    }

    return result;
  }

  toggle(date: NgbDate | null, dppop: NgbInputDatepicker) {
    this.startDate = date ?? this.calendar.getToday();
    dppop.toggle();
  }

  getNgbDpStartDate(
    _startDate: /*eslint-disable-line @typescript-eslint/no-unused-vars*/ NgbDate | null
  ): { year: number; month: number; day?: number } {
    if (this.fromDate !== null) {
      return {
        year: this.fromDate.year,
        month: this.fromDate.month,
        day: this.fromDate.day
      };
    }

    return this.dpConfig.startDate;
  }

  ngOnInit(): void {
    this.allOffices$ = this.store.pipe(select(selectAllOfficeIds));

    this.store.dispatch(new RequestBookingOffices());

    this.allOffices$.subscribe(offices => {
      this.defaultOffices = offices;
      if (this.filterControlGroup) {
        this.filterControlGroup.controls.officeIds.setValue(this.defaultOffices);
      }
    });

    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);

    const sixMonthsPriorToYesterday = new Date(yesterday);
    sixMonthsPriorToYesterday.setDate(yesterday.getDate() - 6 * 30);

    this.fromDate = NgbDate.from({
      day: sixMonthsPriorToYesterday.getDate(),
      month: sixMonthsPriorToYesterday.getMonth() + 1,
      year: sixMonthsPriorToYesterday.getFullYear()
    });

    this.toDate = NgbDate.from({
      day: yesterday.getDate(),
      month: yesterday.getMonth() + 1,
      year: yesterday.getFullYear()
    });

    this.formDefaultValues.startDate = this.ngbDateFormatter.format(this.fromDate);
    this.formDefaultValues.endDate = this.ngbDateFormatter.format(this.toDate);

    this.filterControlGroup = this.formBuilder.group({
      ...this.formDefaultValues,
      destination: this.formBuilder.group({
        ...this.formDestinationDefaultValues
      })
    });

    this.filterControlGroup.controls['officeIds'].addValidators([Validators.required]);
    this.filterControlGroup.controls['startDate'].addValidators([Validators.required]);
    this.filterControlGroup.controls['endDate'].addValidators([Validators.required]);

    this.currentStatisticsEmpty$ = this.store.pipe(select(isCurrentChartEmpty));
    this.oneYearAgoStatisticsEmpty$ = this.store.pipe(select(isOneYearAgoChartEmpty));
    this.twoYearsAgoStatisticsEmpty$ = this.store.pipe(select(isTwoYearsAgoChartEmpty));
    this.officesRequestSending$ = this.store.pipe(select(isOfficesRequestSending));

    this.officesRequestSending$.subscribe(sending => {
      if (!sending) {
        this.applyFilters();
      }
    });
  }

  resetFilters() {
    this.filterControlGroup.reset(this.formDefaultValues);
    this.filterControlGroup.get('destination').setValue(this.formDestinationDefaultValues);
    this.filterControlGroup.controls.officeIds.setValue(this.defaultOffices);
    this.applyFilters();
  }

  applyFilters() {
    if (!this.filterControlGroup.valid) {
      return;
    }

    this.parseInput(this.fromDate, this.filterControlGroup.controls['startDate'].value, 'startDate');
    this.parseInput(this.toDate, this.filterControlGroup.controls['endDate'].value, 'endDate');

    const currentFilter = this.getFilterFromForm();

    const assertedPeriodValues = this.assertTypedPeriodValues(currentFilter);

    this.filterControlGroup.controls.startDate.setValue(assertedPeriodValues.startDate);
    this.filterControlGroup.controls.endDate.setValue(assertedPeriodValues.endDate);

    currentFilter.startDate = assertedPeriodValues.startDate;
    currentFilter.endDate = assertedPeriodValues.endDate;

    const generalFiltersSame = this.areGeneralFiltersSame(currentFilter, this._appliedFilter);
    const comparePeriodYearOffsetSame =
      currentFilter.comparePeriodYearOffset === this._appliedFilter?.comparePeriodYearOffset;

    if (generalFiltersSame && comparePeriodYearOffsetSame) {
      return;
    }

    if (!generalFiltersSame) {
      this.store.dispatch(new ClearAllStatistics());
    }

    this.triggerLoadActions(currentFilter);
    console.log(currentFilter)
    this.appliedFilter = currentFilter;

    this.store.dispatch(new StoreStatisticsFilters(this.appliedFilter));
  }

  private assertTypedPeriodValues(filter: StatisticsFilter): { startDate: string; endDate: string } {
    const ngbStartDateStruct = this.ngbDateFormatter.parse(filter.startDate);
    const ngbEndDateStruct = this.ngbDateFormatter.parse(filter.endDate);

    let ngbStartDate = ngbStartDateStruct ? NgbDate.from(ngbStartDateStruct) : null;
    let ngbEndDate = ngbEndDateStruct ? NgbDate.from(ngbEndDateStruct) : null;

    ngbStartDate = this.calendar.isValid(ngbStartDate) ? ngbStartDate : null;
    ngbEndDate = this.calendar.isValid(ngbEndDate) ? ngbEndDate : null;

    if (!ngbStartDate) {
      ngbStartDate = this.fromDate;
    }

    if (!ngbEndDate) {
      if (this.toDate?.after(ngbStartDate)) {
        ngbEndDate = this.toDate;
      }
    } else if (ngbEndDate.before(ngbStartDate) && !ngbEndDate.equals(ngbStartDate)) {
      ngbEndDate = null;
    }

    return {
      startDate: this.ngbDateFormatter.format(ngbStartDate),
      endDate: this.ngbDateFormatter.format(ngbEndDate)
    };
  }

  private getFilterFromForm(): StatisticsFilter {
    const filterFormValues = this.filterControlGroup.getRawValue();
    const destinationName = filterFormValues.destination.code;
    if (this.toggleNewUi) {
      filterFormValues.officeIds = filterFormValues.officeIds.map((office: ICompanyNameAndOffice) => office.officeId)
    }
    const filter: StatisticsFilter = {
      contentType: filterFormValues.contentType,
      officeIds: filterFormValues.officeIds,
      startDate: this.ngbDateFormatter.format(this.fromDate),
      endDate: this.ngbDateFormatter.format(this.toDate),
      comparePeriodYearOffset: filterFormValues.comparePeriodYearOffset
    };

    filter.destination = {
      type: DestinationType.CITY,
      code: destinationName
    };
    if (this.isTwoLetterCode(destinationName)) {
      filter.destination.type = DestinationType.COUNTRY;
    }

    return filter;
  }

  // check if general StatisticFilters are the same
  // this means all filters exluding comparePeriodYearOffset
  private areGeneralFiltersSame(current: StatisticsFilter, previous: StatisticsFilter): boolean {
    const currentCopy: StatisticsFilter = { ...current, comparePeriodYearOffset: null };
    const previousCopy: StatisticsFilter = { ...previous, comparePeriodYearOffset: null };
    return JSON.stringify(currentCopy) === JSON.stringify(previousCopy);
  }

  private triggerLoadActions(filter: StatisticsFilter): void {
    const comparePeriodYearOffset = filter.comparePeriodYearOffset;

    this.currentStatisticsEmpty$.pipe(take(2)).subscribe(empty => {
      if (empty) {
        this.store.dispatch(new SetCurrentChart(filter));
        this.store.dispatch(
          new SetTopRates(filter, {
            ...filter,
            standardRates: true,
            statisticType: null,
            destinationType: null
          })
        );
      }
    });

    if (comparePeriodYearOffset > 0) {
      this.oneYearAgoStatisticsEmpty$.pipe(take(2)).subscribe(empty => {
        if (empty) {
          this.store.dispatch(new SetOneYearAgoChart(filter));
        }
      });
    }

    if (comparePeriodYearOffset > 1) {
      this.twoYearsAgoStatisticsEmpty$.pipe(take(2)).subscribe(emtpy => {
        if (emtpy) {
          this.store.dispatch(new SetTwoYearsAgoChart(filter));
        }
      });
    }
  }

  private parseInput(previous: NgbDate | null, typed: string, formControlName: string) {
    const parsed = this.ngbDateFormatter.parse(typed);

    const parsedInputIsValid = parsed && this.calendar.isValid(NgbDate.from(parsed));

    if (formControlName === 'startDate') {
      this.fromDate = parsedInputIsValid ? NgbDate.from(parsed) : previous;
    } else if (formControlName === 'endDate') {
      this.toDate = parsedInputIsValid ? NgbDate.from(parsed) : previous;
    }

    if (parsedInputIsValid && this.fromDate && this.toDate?.before(this.fromDate)) {
      this.toDate = this.fromDate;
    }
  }

  private isTwoLetterCode(input: string): boolean {
    return this.TWO_LETTER_CODE.test(input);
  }
}
